import { CheckCircle2, BarChart3, Search, Cpu, Brain, Lightbulb } from 'lucide-react';
import ScrollReveal from './ScrollReveal';

const capabilities = [
{ icon: BarChart3, label: 'Crawler Vision Score', desc: 'A single 0-100 metric capturing your overall crawl health' },
  { icon: Search, label: 'Crawlability Analysis', desc: 'Full audit of how bots traverse your site structure' },
  { icon: Cpu, label: 'Rendering Diagnostics', desc: 'See exactly what bots see after JavaScript execution' },
  { icon: Brain, label: 'AI Readiness Evaluation', desc: 'Check if ChatGPT, Gemini, Claude and Grok know your site' },
  { icon: Lightbulb, label: 'Actionable Recommendations', desc: 'Prioritized fixes with step-by-step implementation guides' },
];

const SolutionSection = () => (
  <section className="py-20 md:py-24 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: text */}
        <div>
          <ScrollReveal direction="left">
            <span className="inline-block px-4 py-1.5 bg-cv-sky/10 text-cv-navy text-xs font-bold uppercase tracking-wider rounded-full border border-cv-sky/30 mb-4">
              The Solution
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-cv-navy mb-6">
              See Your Website Through the Eyes of{' '}
              <span className="text-cv-navy">Bots &amp; AI</span>
            </h2>
            <p className="text-cv-navy/70 text-lg mb-8 leading-relaxed">
              Crawler Vision combines deep crawl analysis, JavaScript rendering, and AI visibility checks into one powerful platform —
              giving you the complete picture no other tool can.
            </p>

            <div className="space-y-5">
              {capabilities.map((c, i) => (
                <ScrollReveal key={c.label} delay={i * 0.07} direction="left">
                  <div className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-cv-sky/10 border border-cv-sky/30 flex items-center justify-center group-hover:shadow-md transition-shadow">
                      <c.icon className="w-5 h-5 text-cv-navy" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-cv-navy">{c.label}</h3>
                      <p className="text-sm text-cv-navy/60">{c.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </ScrollReveal>
        </div>

        {/* Right: visual dashboard mockup */}
        <ScrollReveal direction="right">
          <div className="relative">
            <div className="bg-cv-sky/10 rounded-3xl border border-cv-navy/15 shadow-xl p-6 sm:p-8">
              {/* Mock score display */}
                <div className="bg-white rounded-2xl shadow-sm border border-cv-navy/10 p-6 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-cv-navy/40">Crawler Vision Score</span>
                  <span className="px-2 py-0.5 bg-cv-teal/20 text-cv-navy text-xs font-bold rounded-full">Good</span>
                </div>
                <div className="flex items-end gap-3">
                  <span className="text-5xl font-extrabold text-cv-navy">78</span>
                  <span className="text-cv-navy/40 text-sm mb-2">/ 100</span>
                </div>
                <div className="mt-3 h-2 bg-cv-navy/10 rounded-full overflow-hidden">
                  <div className="h-full w-[78%] bg-cv-navy rounded-full" />
                </div>
              </div>

              {[
                { label: 'Technical SEO', score: 85, color: 'bg-cv-teal' },
                { label: 'AIO Readiness', score: 72, color: 'bg-cv-navy' },
                { label: 'Meta Relevancy', score: 90, color: 'bg-cv-teal' },
                { label: 'AI Files', score: 65, color: 'bg-cv-gold' },
                { label: 'GEO / Local', score: 58, color: 'bg-cv-orange' },
              ].map((m) => (
                <div key={m.label} className="flex items-center gap-3 py-2">
                  <span className="text-xs font-medium text-cv-navy/70 w-28 flex-shrink-0">{m.label}</span>
                  <div className="flex-1 h-1.5 bg-cv-navy/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${m.color} rounded-full`}
                      style={{ width: `${m.score}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-cv-navy/80 w-8 text-right">{m.score}</span>
                </div>
              ))}

              {/* LLM badges */}
              <div className="flex flex-wrap gap-2 mt-4">
                {[
                  { name: 'ChatGPT', status: true },
                  { name: 'Gemini', status: true },
                  { name: 'Claude', status: false },
                  { name: 'Grok', status: true },
                ].map((llm) => (
                  <span
                    key={llm.name}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      llm.status
                        ? 'bg-cv-teal/10 text-cv-navy border border-cv-teal/40'
                        : 'bg-cv-orange/10 text-cv-orange border border-cv-orange/40'
                    }`}
                  >
                    {llm.status ? <CheckCircle2 className="w-3 h-3" /> : '✕'} {llm.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Decorative glow */}
            <div className="absolute -inset-4 bg-cv-sky/30 rounded-3xl -z-10 blur-xl opacity-40" />
          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
);

export default SolutionSection;
